import { strToU8, zipSync } from 'fflate';
import { ColumnType, insightScope, relationships, } from './data-sources.js';
export const insightUrl = (connection, source) => {
    const url = new URL(`${connection.apiBase}/v1/${connection.tenantId}/insights/${source.id}`);
    url.searchParams.set('lang', connection.lang);
    if (connection.creditorId !== undefined) {
        url.searchParams.set('creditorId', connection.creditorId);
    }
    return url.toString();
};
export const mText = (value) => `"${value.replace(/#/g, '#(#)').replace(/"/g, '""').replace(/\r/g, '#(cr)').replace(/\n/g, '#(lf)').replace(/\t/g, '#(tab)')}"`;
const mTypes = {
    [ColumnType.Text]: 'type text',
    [ColumnType.Number]: 'type number',
    [ColumnType.Integer]: 'Int64.Type',
    [ColumnType.Boolean]: 'type logical',
    [ColumnType.DateTime]: 'type datetime',
    [ColumnType.Json]: 'type text',
};
export const powerQuery = (connection, source, key) => {
    const query = [`lang = ${mText(connection.lang)}`];
    if (connection.creditorId !== undefined) {
        query.push(`creditorId = ${mText(connection.creditorId)}`);
    }
    const transforms = source.columns.map((column) => {
        let transform;
        switch (column.type) {
            case ColumnType.Json:
                transform =
                    'each if _ = null then null else Text.FromBinary(Json.FromValue(_), TextEncoding.Utf8)';
                break;
            case ColumnType.DateTime:
                transform =
                    'each if _ = null then null else if Text.Length(_) = 10 then DateTime.FromText(_, [Culture = "en-US"]) else DateTimeZone.RemoveZone(DateTimeZone.ToUtc(DateTimeZone.FromText(_, [Culture = "en-US"])))';
                break;
            case ColumnType.Text:
                transform = 'each if _ = null then null else Text.From(_, "en-US")';
                break;
            case ColumnType.Number:
            case ColumnType.Integer:
                transform = 'each if _ = null then null else Number.From(_, "en-US")';
                break;
            case ColumnType.Boolean:
                transform = 'each if _ = null then null else Logical.From(_)';
                break;
        }
        return `{${mText(column.name)}, ${transform}, ${mTypes[column.type]}}`;
    });
    return `let
    Source = Json.Document(Web.Contents(${mText(connection.apiBase)}, [
        RelativePath = ${mText(`v1/${connection.tenantId}/insights/${source.id}`)},
        Query = [${query.join(', ')}],
        Headers = [Authorization = ${mText(`Bearer ${key}`)}, Accept = "application/json"],
        Timeout = #duration(0, 0, 10, 0)
    ])),
    Rows = Table.FromRecords(Source, {${source.columns.map((column) => mText(column.name)).join(', ')}}, MissingField.UseNull),
    Typed = Table.TransformColumns(Rows, {
        ${transforms.join(',\n        ')}
    })
in
    Typed`;
};
const json = (value) => JSON.stringify(value, null, 2);
// TMDL object names need single quotes when they contain '.', '=', ':', a quote or whitespace.
export const tmdlName = (name) => /[.=:'\s]/.test(name) ? `'${name.replace(/'/g, "''")}'` : name;
// TMDL is a tab-indented text format: a multi-line expression sits two
// levels below its property, and child objects one level below their parent.
const tmdlLines = (objects, depth = 0) => objects.flatMap((object) => {
    const indent = '\t'.repeat(depth);
    const { description, properties = {}, expression, children = [] } = object;
    return [
        ...(description === undefined ? [] : [`${indent}/// ${description}`]),
        `${indent}${object.declaration}`,
        ...Object.entries(properties).map(([name, value]) => `${indent}\t${name}: ${value}`),
        ...(expression === undefined
            ? []
            : [
                `${indent}\t${expression.property} =`,
                ...expression.value
                    .split('\n')
                    .map((line) => `${indent}\t\t\t${line}`),
            ]),
        '',
        ...tmdlLines(children, depth + 1),
    ];
});
const tmdl = (objects) => `${tmdlLines(objects).join('\n').trimEnd()}\n`;
const tableTmdl = (description, source, query) => ({
    declaration: `table ${tmdlName(source.id)}`,
    description,
    children: [
        ...source.columns.map((column) => ({
            declaration: `column ${tmdlName(column.name)}`,
            properties: {
                dataType: column.type === ColumnType.Json ? ColumnType.Text : column.type,
                summarizeBy: 'none',
                sourceColumn: column.name,
            },
        })),
        {
            declaration: `partition ${tmdlName(source.id)} = m`,
            properties: { mode: 'import' },
            expression: { property: 'source', value: query },
        },
    ],
});
export const projectFiles = (lang, connection, sources, key) => {
    const labels = lang.insights.powerbi;
    const sourceIds = new Set(sources.map((source) => source.id));
    const modelRelationships = relationships.filter((relationship) => sourceIds.has(relationship.fromTable) &&
        sourceIds.has(relationship.toTable));
    const queries = Object.fromEntries(sources.map((source) => [source.id, powerQuery(connection, source, key)]));
    return {
        'Debbie.pbip': json({
            $schema: 'https://developer.microsoft.com/json-schemas/fabric/pbip/pbipProperties/1.0.0/schema.json',
            version: '1.0',
            artifacts: [{ report: { path: 'Debbie.Report' } }],
            settings: { enableAutoRecovery: false },
        }),
        'Debbie.Report/definition.pbir': json({
            $schema: 'https://developer.microsoft.com/json-schemas/fabric/item/report/definitionProperties/2.0.0/schema.json',
            version: '4.0',
            datasetReference: { byPath: { path: '../Debbie.SemanticModel' } },
        }),
        'Debbie.Report/definition/version.json': json({
            $schema: 'https://developer.microsoft.com/json-schemas/fabric/item/report/definition/versionMetadata/1.0.0/schema.json',
            version: '4.0.0',
        }),
        'Debbie.Report/definition/report.json': json({
            $schema: 'https://developer.microsoft.com/json-schemas/fabric/item/report/definition/report/1.0.0/schema.json',
            themeCollection: {},
            layoutOptimization: 'None',
        }),
        'Debbie.Report/definition/pages/Overview/page.json': json({
            $schema: 'https://developer.microsoft.com/json-schemas/fabric/item/report/definition/page/1.0.0/schema.json',
            name: 'Overview',
            displayName: 'Debbie',
            displayOption: 'FitToPage',
            height: 720,
            width: 1280,
        }),
        // Version 4.0 lets the semantic model be stored as a TMDL definition folder.
        'Debbie.SemanticModel/definition.pbism': json({
            $schema: 'https://developer.microsoft.com/json-schemas/fabric/item/semanticModel/definitionProperties/1.0.0/schema.json',
            version: '4.0',
            settings: {},
        }),
        'Debbie.SemanticModel/definition/database.tmdl': tmdl([
            { declaration: 'database', properties: { compatibilityLevel: '1600' } },
        ]),
        'Debbie.SemanticModel/definition/model.tmdl': tmdl([
            {
                declaration: 'model Model',
                properties: {
                    culture: 'en-US',
                    defaultPowerBIDataSourceVersion: 'powerBI_V3',
                    sourceQueryCulture: 'en-US',
                },
            },
            {
                declaration: `annotation PBI_QueryOrder = ${JSON.stringify(sources.map((source) => source.id))}`,
            },
            ...sources.map((source) => ({
                declaration: `ref table ${tmdlName(source.id)}`,
            })),
        ]),
        ...(modelRelationships.length > 0 && {
            // Many-to-one, single-direction and active are the TMDL defaults.
            'Debbie.SemanticModel/definition/relationships.tmdl': tmdl(modelRelationships.map((relationship) => ({
                declaration: `relationship ${tmdlName(`${relationship.fromTable}_${relationship.fromColumn}_${relationship.toTable}`)}`,
                properties: {
                    fromColumn: `${tmdlName(relationship.fromTable)}.${tmdlName(relationship.fromColumn)}`,
                    toColumn: `${tmdlName(relationship.toTable)}.${tmdlName(relationship.toColumn)}`,
                },
            }))),
        }),
        ...Object.fromEntries(sources.map((source) => [
            `Debbie.SemanticModel/definition/tables/${source.id}.tmdl`,
            tmdl([
                tableTmdl(labels.sourceDescriptions[source.id], source, queries[source.id]),
            ]),
        ])),
        'README.txt': [
            'Debbie · Power BI',
            labels.securityWarning,
            labels.openInstructions,
            labels.manualInstructions,
            labels.refreshInstructions,
            labels.dictionaryInstructions,
            labels.modelNotes,
            `${lang.apiSettings.keys}: ${key.split(':')[0]}`,
        ].join('\n\n'),
        'data-dictionary.json': json({
            version: 1,
            tenantId: connection.tenantId,
            creditorId: connection.creditorId ?? null,
            language: connection.lang,
            notes: labels.modelNotes,
            sources: sources.map((source) => ({
                ...source,
                label: lang.insights.apiTypes[source.id],
                description: labels.sourceDescriptions[source.id],
                url: insightUrl(connection, source),
                scope: insightScope(source.id),
            })),
            relationships: modelRelationships.map((relationship) => ({
                ...relationship,
                cardinality: 'many-to-one',
                filterDirection: 'one-to-many',
            })),
        }),
        ...Object.fromEntries(sources.map((source) => [`queries/${source.id}.pq`, queries[source.id]])),
    };
};
export const projectArchive = (files) => zipSync(Object.fromEntries(Object.entries(files).map(([path, content]) => [path, strToU8(content)])));
export const downloadProject = (archive, name) => {
    const url = URL.createObjectURL(new Blob([new Uint8Array(archive)], { type: 'application/zip' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
};
//# sourceMappingURL=project.js.map